import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";

/**
 * BannerAdRotator
 * Public banner display. Rotates active banners for the given `placement`
 * every 8 seconds. Silently renders nothing if there are no active ads —
 * a totally non-intrusive component.
 *
 * @param {string} placement — "home" or "category:<slug>"
 */
export default function BannerAdRotator({ placement = "home" }) {
  const [ads, setAds] = useState([]);
  const [idx, setIdx] = useState(0);
  const seenRef = useRef(new Set());

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/banner-ads/active`, { params: { placement } })
      .then((r) => {
        if (alive) setAds(r.data?.items || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [placement]);

  // Rotate every 8s
  useEffect(() => {
    if (ads.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ads.length), 8000);
    return () => clearInterval(t);
  }, [ads.length]);

  // Fire impression once per ad per mount
  useEffect(() => {
    const ad = ads[idx];
    if (!ad) return;
    if (seenRef.current.has(ad.ad_id)) return;
    seenRef.current.add(ad.ad_id);
    axios.post(`${API}/banner-ads/${ad.ad_id}/impression`).catch(() => {});
  }, [idx, ads]);

  if (!ads.length) return null;
  const ad = ads[idx];
  const imageSrc = ad.image_url?.startsWith("/uploads") ? `/api${ad.image_url}` : ad.image_url;

  const handleClick = () => {
    if (ad.ad_id) axios.post(`${API}/banner-ads/${ad.ad_id}/click`).catch(() => {});
  };

  const content = (
    <div
      className="relative w-full aspect-[5/1] sm:aspect-[6/1] rounded-2xl overflow-hidden bg-tbc-cream border border-tbc-gold/30 shadow-sm"
      data-testid={`banner-ad-${ad.ad_id}`}
    >
      <img
        src={imageSrc}
        alt={ad.alt_text || ad.business_name || "Sponsored banner"}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      <div className="absolute top-2 right-3 text-[10px] uppercase tracking-widest bg-black/40 text-white px-2 py-0.5 rounded-full font-body">
        Sponsored
      </div>
      {ads.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
          {ads.map((a, i) => (
            <span
              key={a.ad_id || `ad-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-6 bg-tbc-gold" : "w-1.5 bg-white/60"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );

  if (ad.link_url) {
    return (
      <a
        href={ad.link_url}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={handleClick}
        className="block"
      >
        {content}
      </a>
    );
  }
  return content;
}
